import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowLeft, GraduationCap } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useGuide } from "@/lib/guide";
import { ModuleView } from "@/components/guide/ModuleView";
import { AskGuide } from "@/components/guide/AskGuide";

const TABS = [["page", "This page"], ["all", "All guides"], ["ask", "Ask"]];

const AllGuides = ({ onPick }) => {
  const { guide, done } = useGuide();
  const [q, setQ] = useState("");
  const list = useMemo(() => guide.modules.filter((m) => !q || JSON.stringify(m).toLowerCase().includes(q.toLowerCase())), [guide, q]);
  return (
    <div className="space-y-4">
      <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guides…" data-testid="guide-search"
          className="w-full pl-9 h-10 rounded-full bg-white/[0.03] border border-white/10 text-sm outline-none focus:border-white/30" /></div>
      {guide.tracks.map((t) => {
        const ms = list.filter((m) => m.track === t.id);
        if (!ms.length) return null;
        return (
          <div key={t.id}><div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5">{t.title}</div>
            {ms.map((m) => (
              <button key={m.id} onClick={() => onPick(m.id)} data-testid={`guide-pick-${m.id}`} className="w-full text-left rounded-lg px-3 py-2.5 hover:bg-white/[0.04] transition-colors flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: done.includes(m.id) ? "#D4FF3A" : "#3A3A46" }} />
                <span className="min-w-0"><span className="text-sm block">{m.title}</span><span className="text-xs text-muted-foreground line-clamp-2">{m.summary}</span></span>
              </button>
            ))}
          </div>
        );
      })}
      {list.length === 0 && <p className="text-sm text-muted-foreground">No guide matches. Try the Ask tab.</p>}
    </div>
  );
};

export const GuideDrawer = () => {
  const { guide, open, setOpen, tab, setTab, moduleId, setModuleId, current } = useGuide();
  if (!guide) return null;
  const picked = moduleId && guide.modules.find((m) => m.id === moduleId);
  const pageModule = current || guide.modules.find((m) => m.id === "welcome");
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-xl bg-[#07070B] border-white/10 flex flex-col p-0" data-testid="guide-drawer">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-white/[0.06] text-left">
          <SheetTitle className="font-display text-2xl font-normal flex items-center gap-2"><GraduationCap className="w-5 h-5 text-[#D4FF3A]" />Learn Manahouse</SheetTitle>
          <SheetDescription className="text-xs">Guides, examples and tours for every part of the platform.</SheetDescription>
          <div className="flex gap-1 p-1 rounded-full bg-white/[0.04] border border-white/10 w-fit mt-2">
            {TABS.map(([k, t]) => <button key={k} onClick={() => { setTab(k); setModuleId(null); }} data-testid={`guide-tab-${k}`}
              className={`h-8 px-3.5 rounded-full text-xs transition-colors ${tab === k ? "bg-white text-black" : "text-muted-foreground hover:text-foreground"}`}>{t}</button>)}
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {tab === "ask" ? <AskGuide /> : picked ? (
            <div className="space-y-4"><button onClick={() => setModuleId(null)} className="inline-flex items-center gap-1 font-mono text-[11px] text-muted-foreground hover:text-foreground" data-testid="guide-back"><ArrowLeft className="w-3.5 h-3.5" />All guides</button><ModuleView m={picked} compact /></div>
          ) : tab === "all" ? <AllGuides onPick={setModuleId} /> : <ModuleView m={pageModule} compact />}
        </div>
        <div className="px-5 py-3 border-t border-white/[0.06]">
          <Link to="/academy" onClick={() => setOpen(false)} className="font-mono text-[11px] underline text-muted-foreground hover:text-foreground" data-testid="guide-open-academy">Open the full Academy →</Link>
        </div>
      </SheetContent>
    </Sheet>
  );
};
